import React, { useEffect, useState } from "react";
import { api } from "@/skate/lib/api";
import { useAuth } from "@/skate/context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import SkaterAvatar from "@/skate/components/SkaterAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Search, MapPin, Calendar, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";

const FORMATS = [
  { v: "GAME_OF_SKATE", l: "S.K.A.T.E", d: "Letters battle" },
  { v: "BEST_TRICK",    l: "BEST TRICK", d: "Highest hammer wins" },
  { v: "SESSION_1V1",   l: "1V1 SESSION", d: "Free format" },
];

export const CreateChallenge = () => {
  const { user } = useAuth();
  const { opponentId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(opponentId ? 1 : 0);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [opp, setOpp] = useState(null);
  const [format, setFormat] = useState("GAME_OF_SKATE");
  const [location, setLocation] = useState("");
  const [spot, setSpot] = useState("");
  const [when, setWhen] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (q.length < 2) { setResults([]); return; }
      const { data } = await api.get(`/users?q=${encodeURIComponent(q)}&limit=20`);
      setResults(data.filter((u) => u.user_id !== user.user_id));
    }, 200);
    return () => clearTimeout(t);
  }, [q, user]);

  useEffect(() => {
    if (opponentId) {
      (async () => {
        try {
          const { data } = await api.get(`/users/${opponentId}`);
          setOpp(data);
        } catch {}
      })();
    }
  }, [opponentId]);

  const submit = async () => {
    if (!opp) return toast.error("Pick an opponent");
    if (!location.trim()) return toast.error("Set a location");
    if (!when) return toast.error("Pick a date/time");
    setSubmitting(true);
    try {
      await api.post("/skate/challenges", {
        opponent_id: opp.user_id,
        format,
        location: location.trim(),
        spot_name: spot.trim() || null,
        scheduled_at: new Date(when).toISOString(),
        note: note.trim(),
      });
      toast.success("Challenge sent. Waiting for opponent.");
      navigate("/skate/challenges");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="create-challenge-page">
      <button onClick={() => navigate(-1)} className="font-mono text-[10px] tracking-widest text-zinc-500 mb-4 inline-flex items-center gap-1 hover:text-white">
        <ArrowLeft className="w-3 h-3" /> BACK
      </button>
      <h1 className="font-display text-5xl leading-none mb-6">CALL OUT<br/><span className="text-[#FACC15]">A SKATER.</span></h1>

      {/* Step 0: Pick opponent */}
      {step === 0 && (
        <div>
          <div className="border border-zinc-800 p-3 flex items-center gap-2 mb-3">
            <Search className="w-4 h-4 text-zinc-500" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search username or name…"
              data-testid="search-opponent-input"
              className="flex-1 bg-transparent outline-none font-mono text-sm placeholder:text-zinc-600"
            />
          </div>
          <div className="space-y-2">
            {results.map((u) => (
              <button
                key={u.user_id}
                onClick={() => { setOpp(u); setStep(1); }}
                data-testid={`opponent-${u.user_id}`}
                className="w-full border border-zinc-800 p-3 flex items-center gap-3 hover:border-[#FACC15] transition-colors text-left"
              >
                <SkaterAvatar user={u} size={40} />
                <div className="flex-1">
                  <div className="font-display text-lg">@{u.username}</div>
                  <div className="font-mono text-[10px] text-zinc-500">{u.elo} ELO · {u.city}</div>
                </div>
              </button>
            ))}
            {q.length >= 2 && results.length === 0 && (
              <div className="font-mono text-xs text-zinc-500 p-4">NO MATCHES</div>
            )}
          </div>
        </div>
      )}

      {/* Step 1: Configure */}
      {step === 1 && (
        <div className="space-y-5">
          {opp && (
            <div className="border border-zinc-800 p-4 flex items-center gap-3 bg-[#121212]">
              <SkaterAvatar user={opp} size={48} />
              <div className="flex-1">
                <div className="font-display text-xl">@{opp.username}</div>
                <div className="font-mono text-[10px] text-zinc-500">{opp.elo} ELO · {opp.city}</div>
              </div>
              {!opponentId && (
                <button onClick={() => { setOpp(null); setStep(0); }} className="font-mono text-[10px] tracking-widest text-zinc-500 hover:text-white" data-testid="change-opponent">
                  CHANGE
                </button>
              )}
            </div>
          )}

          <div>
            <label className="font-mono text-[10px] tracking-widest text-zinc-500 block mb-2">FORMAT</label>
            <div className="grid grid-cols-3 gap-2">
              {FORMATS.map((f) => (
                <button
                  key={f.v}
                  onClick={() => setFormat(f.v)}
                  data-testid={`format-${f.v}`}
                  className={`border p-3 text-left ${format === f.v ? "border-[#FACC15] bg-[#FACC15]/5" : "border-zinc-800 hover:border-zinc-600"}`}
                >
                  <div className="font-display text-base">{f.l}</div>
                  <div className="font-mono text-[9px] tracking-widest text-zinc-500 mt-1">{f.d}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] tracking-widest text-zinc-500 block mb-2">CITY / AREA</label>
            <Input data-testid="location-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Barcelona" className="bg-[#121212] border-zinc-800 rounded-none h-12 font-mono focus:border-[#FACC15] focus:ring-0" />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-widest text-zinc-500 block mb-2">SPOT NAME (OPTIONAL)</label>
            <Input data-testid="spot-input" value={spot} onChange={(e) => setSpot(e.target.value)} placeholder="MACBA / Paral·lel" className="bg-[#121212] border-zinc-800 rounded-none h-12 font-mono focus:border-[#FACC15] focus:ring-0" />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-widest text-zinc-500 block mb-2">WHEN</label>
            <Input data-testid="when-input" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="bg-[#121212] border-zinc-800 rounded-none h-12 font-mono focus:border-[#FACC15] focus:ring-0 [color-scheme:dark]" />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-widest text-zinc-500 block mb-2">NOTE</label>
            <Textarea data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Best of one. No mercy." className="bg-[#121212] border-zinc-800 rounded-none font-mono focus:border-[#FACC15] focus:ring-0" rows={2} />
          </div>

          <Button
            data-testid="send-challenge-btn"
            onClick={submit}
            disabled={submitting}
            className="w-full bg-[#FACC15] hover:bg-[#FDE047] text-black rounded-none h-14 font-display tracking-widest text-lg"
          >
            {submitting ? "SENDING…" : "SEND CHALLENGE"}
            <Check className="ml-2 w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default CreateChallenge;
